// v1.3.51 - Absolute FQN no imports
package com.terapia.terasenior.ui.therapy

import androidx.compose.animation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material.icons.filled.Save
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.terapia.terasenior.data.repository.results.SupabaseResultsRepository
import com.terapia.terasenior.domain.model.therapy.TherapySession
import com.terapia.terasenior.domain.model.therapy.TherapySessionExercise
import com.terapia.terasenior.domain.usecase.results.SaveActivityResultUseCase
import com.terapia.terasenior.treatment.ui.*
import com.terapia.terasenior.treatment.repository.TherapeuticExerciseCatalog
import com.terapia.terasenior.treatment.repository.LiteracyExerciseCatalog
import com.terapia.terasenior.treatment.repository.PerceptionExerciseCatalog
import com.terapia.terasenior.treatment.repository.LanguageExerciseCatalog
import com.terapia.terasenior.treatment.repository.AttentionExerciseCatalog
import com.terapia.terasenior.treatment.repository.MemoryExerciseCatalog
import com.terapia.terasenior.treatment.repository.OrientationExerciseCatalog
import com.terapia.terasenior.ui.components.LocalTeraseniorLayout
import com.terapia.terasenior.ui.components.ScrollPositionBar
import com.terapia.terasenior.ui.components.accessibility.SpeechManager
private fun safeDurationSeconds(startTimeMs: Long): Int =
    ((activityTimeMillis() - startTimeMs) / 1000).toInt().coerceAtLeast(1)

@Composable
fun SessionRunnerScreen(
    viewModel: SessionRunnerViewModel,
    onFinished: () -> Unit
) {
    val uiState by viewModel.uiState.collectAsState()
    val speechManager = remember { SpeechManager() }

    Box(modifier = Modifier.fillMaxSize()) {
        when (val state = uiState) {
            is SessionRunnerUiState.Loading -> {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator()
                }
            }
            is SessionRunnerUiState.Transition -> {
                TransitionView(
                    state = state,
                    onStart = { viewModel.startExercise(state.nextIndex) },
                    speechManager = speechManager
                )
            }
            is SessionRunnerUiState.Playing -> {
                val currentExercise = state.exercises[state.currentIndex]
                
                ExerciseRouter(
                    exercise = currentExercise,
                    sessionId = state.session.id,
                    patientId = state.session.patientId,
                    professionalId = state.session.therapistId,
                    appointmentId = state.session.appointmentId,
                    onExerciseCompleted = { hits, errors, duration -> 
                        viewModel.nextExercise(hits = hits, errors = errors, duration = duration) 
                    },
                    onAbort = { viewModel.abortSession() }
                )

                ProfessionalHUD(onShowPanel = { viewModel.toggleProfessionalPanel() })

                if (state.showProfessionalPanel) {
                    ProfessionalControlPanel(
                        onLogAssistance = { viewModel.logAssistance(it, null) },
                        onLogIncident = { viewModel.logIncident(it, null) },
                        onDismiss = { viewModel.toggleProfessionalPanel() }
                    )
                }
            }
            is SessionRunnerUiState.Summary -> {
                ClinicalValuationView(
                    session = state.session,
                    hits = state.hits,
                    errors = state.errors,
                    durationSeconds = state.durationSeconds,
                    resultsCount = state.resultsCount,
                    areaSummaries = state.areaSummaries,
                    onSave = { p, f, n -> viewModel.finishSession(p, f, n, state.hits, state.errors, state.durationSeconds) }
                )
            }
            is SessionRunnerUiState.Finished -> {
                onFinished()
            }
            is SessionRunnerUiState.Error -> {
                Box(modifier = Modifier.fillMaxSize().padding(24.dp), contentAlignment = Alignment.Center) {
                    Text(state.message, color = MaterialTheme.colorScheme.error)
                }
            }
        }
    }
}

@Composable
private fun ClinicalValuationView(
    session: TherapySession,
    hits: Int,
    errors: Int,
    durationSeconds: Int,
    resultsCount: Int = 0,
    areaSummaries: List<CognitiveAreaSummary> = emptyList(),
    onSave: (participation: String, fatigue: String, notes: String) -> Unit
) {
    val layout = LocalTeraseniorLayout.current
    var participation by remember { mutableStateOf("MEDIUM") }
    var fatigue by remember { mutableStateOf("NONE") }
    var notes by remember { mutableStateOf("") }
    val scrollState = rememberScrollState()

    val minutes = durationSeconds / 60
    val seconds = durationSeconds % 60

    Box(
        modifier = Modifier
            .fillMaxSize()
            .padding(horizontal = if (layout.isCompactWidth) 16.dp else 24.dp, vertical = if (layout.isCompactHeight) 16.dp else 24.dp),
        contentAlignment = Alignment.TopCenter
    ) {
    Column(
        modifier = Modifier
            .widthIn(max = 860.dp)
            .fillMaxWidth()
            .fillMaxSize(),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(scrollState)
                .padding(bottom = 32.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
        Text("Cierre de Sesión Terapéutica", style = MaterialTheme.typography.headlineMedium, fontWeight = FontWeight.Bold)
        
        Spacer(modifier = Modifier.height(8.dp))
        Text("Valoración profesional de la intervención.", color = Color.Gray)
        
        Spacer(modifier = Modifier.height(if (layout.isCompactHeight) 16.dp else 32.dp))

        Card(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(24.dp)) {
            Column(
                modifier = Modifier.padding(if (layout.isCompactHeight) 20.dp else 24.dp),
                verticalArrangement = Arrangement.spacedBy(if (layout.isCompactHeight) 16.dp else 24.dp)
            ) {
                
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceEvenly) {
                    StatBox(label = "Aciertos", value = hits.toString(), color = Color(0xFF2E7D32))
                    StatBox(label = "Errores", value = errors.toString(), color = MaterialTheme.colorScheme.error)
                    StatBox(label = "Tiempo", value = "${minutes}m ${seconds}s", color = MaterialTheme.colorScheme.primary)
                }

                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))

                if (session.isStandardized && areaSummaries.isNotEmpty()) {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Text("Resultados por área cognitiva", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                        areaSummaries.forEach { area ->
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                Text(area.area)
                                Text("${area.hits} aciertos · ${area.errors} fallos · ${area.durationSeconds}s", fontWeight = FontWeight.Medium)
                            }
                        }
                    }
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))
                }

                Column {
                    Text("Nivel de Participación", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.padding(top = 8.dp)) {
                        FilterChip(selected = participation == "LOW", onClick = { participation = "LOW" }, label = { Text("Baja") })
                        FilterChip(selected = participation == "MEDIUM", onClick = { participation = "MEDIUM" }, label = { Text("Media") })
                        FilterChip(selected = participation == "HIGH", onClick = { participation = "HIGH" }, label = { Text("Alta") })
                    }
                }

                Column {
                    Text("Nivel de Fatiga", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.padding(top = 8.dp)) {
                        FilterChip(selected = fatigue == "NONE", onClick = { fatigue = "NONE" }, label = { Text("Ninguna") })
                        FilterChip(selected = fatigue == "MODERATE", onClick = { fatigue = "MODERATE" }, label = { Text("Moderada") })
                        FilterChip(selected = fatigue == "HIGH", onClick = { fatigue = "HIGH" }, label = { Text("Alta") })
                    }
                }

                OutlinedTextField(
                    value = notes,
                    onValueChange = { notes = it },
                    label = { Text("Observaciones Clínicas") },
                    placeholder = { Text("Ej: Ha necesitado apoyo verbal constante en el cálculo...") },
                    modifier = Modifier.fillMaxWidth(),
                    minLines = if (layout.isCompactHeight) 3 else 4,
                    shape = RoundedCornerShape(16.dp)
                )
            }
        }

        Spacer(modifier = Modifier.height(if (layout.isCompactHeight) 16.dp else 24.dp))
        }

        ScrollPositionBar(
            state = scrollState,
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .padding(horizontal = 12.dp)
        )
        }

        Button(
            onClick = { onSave(participation, fatigue, notes) },
            modifier = Modifier.height(if (layout.isCompactHeight) 56.dp else 64.dp).width(280.dp),
            shape = RoundedCornerShape(20.dp)
        ) {
            Icon(Icons.Default.Save, contentDescription = null)
            Spacer(modifier = Modifier.width(12.dp))
            Text("Finalizar y Guardar", fontSize = 18.sp)
        }
    }
    }
}

@Composable
private fun ExerciseRouter(
    exercise: TherapySessionExercise,
    sessionId: String,
    patientId: String?,
    professionalId: String,
    appointmentId: String?,
    onExerciseCompleted: (hits: Int, errors: Int, duration: Int) -> Boolean,
    onAbort: () -> Unit
) {
    val challengeLevel = GdsDifficulty.challengeLevel(exercise.level)
    val resultsRepo = remember { SupabaseResultsRepository() }
    val saveUseCase = remember { SaveActivityResultUseCase(resultsRepo) }

    when {
        exercise.exerciseType.startsWith("orientation") || OrientationExerciseCatalog.contains(exercise.exerciseType) || TherapeuticExerciseCatalog.contains(exercise.exerciseType) || LiteracyExerciseCatalog.contains(exercise.exerciseType) || PerceptionExerciseCatalog.contains(exercise.exerciseType) || LanguageExerciseCatalog.contains(exercise.exerciseType) || AttentionExerciseCatalog.contains(exercise.exerciseType) || MemoryExerciseCatalog.contains(exercise.exerciseType) -> {
            val gameViewModel = remember(exercise.id) { OrientationViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { gameViewModel.startNewGame(exercise.exerciseType, challengeLevel, sessionId) }
            val gameState by gameViewModel.uiState.collectAsState()
            OrientationGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort,
                onCompleted = {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    onExerciseCompleted(gameState.hitsCount, gameState.errorsCount, duration)
                }
            )
        }
        exercise.exerciseType == "number_search" -> {
            val gameViewModel = remember { NumberSearchViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { gameViewModel.startNewGame(challengeLevel, sessionId) }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = gameState.foundCount
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            NumberSearchGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
        exercise.exerciseType in listOf(
            "attention_spot_odd_one_out", "attention_different", "attention_equals_model", 
            "attention_positions", "attention_letters", "attention_numbers", 
            "attention_symbols", "attention_matrices", "attention_row_cancel", 
            "attention_consecutive", "attention_yes_no", "attention_dual_task", 
            "attention_count", "attention_word_search", "attention_differences", "attention_longest"
        ) -> {
            val gameViewModel = remember { VisualAttentionViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { 
                val variation = if (exercise.exerciseType == "attention_spot_odd_one_out") "attention_different" else exercise.exerciseType
                gameViewModel.startNewGame(variation, challengeLevel, sessionId) 
            }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = gameState.foundCount
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            VisualAttentionGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
        exercise.exerciseType == "memory_pairs" -> {
            val gameViewModel = remember { PairsViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { gameViewModel.startNewGame(challengeLevel, sessionId) }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = gameState.pairsFound
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            PairsGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
        exercise.exerciseType.startsWith("memory") -> {
            val gameViewModel = remember(exercise.id) { MemoryViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { gameViewModel.startNewGame(exercise.exerciseType, challengeLevel, sessionId) }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = if (gameState.errorsCount == 0) 1 else 0
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            MemoryGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
        exercise.exerciseType == "language_word_image" -> {
            val gameViewModel = remember { WordImageViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { gameViewModel.startNewGame(challengeLevel, sessionId) }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = if (gameState.errorsCount == 0) 1 else 0
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            WordImageGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
        exercise.exerciseType in listOf("language_denomination", "language_naming_objects") -> {
            val gameViewModel = remember { NamingObjectsViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { gameViewModel.startNewGame(challengeLevel, sessionId) }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = if (gameState.errorsCount == 0) 1 else 0
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            NamingObjectsGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
        exercise.exerciseType in listOf(
            "language_start_letter", "language_start_syllable", 
            "language_end_letter", "language_end_syllable", "language_complex_cluster", 
            "language_semantic_completion", "language_semantic_naming"
        ) -> {
            val gameViewModel = remember { LanguageViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { 
                gameViewModel.startNewGame(exercise.exerciseType, challengeLevel, sessionId, exercise.configuration) 
            }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = if (gameState.errorsCount == 0) 1 else 0
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            LanguageGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
        exercise.exerciseType == "language_semantic_category" -> {
            val gameViewModel = remember { SemanticCategoryViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { gameViewModel.startNewGame(challengeLevel, sessionId) }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = if (gameState.errorsCount == 0) 1 else 0
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            SemanticCategoryGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
        exercise.exerciseType == "calculation_simple" -> {
            val gameViewModel = remember { CalculationViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { gameViewModel.startNewGame(challengeLevel, sessionId) }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = if (gameState.errorsCount == 0) 1 else 0
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            CalculationGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
        exercise.exerciseType == "executive_color_shape_sequence" -> {
            val gameViewModel = remember { ColorShapeSequenceViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { gameViewModel.startNewGame(challengeLevel, sessionId) }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = if (gameState.errorsCount == 0) 1 else 0
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            ColorShapeSequenceGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
        exercise.exerciseType in listOf(
            "executive_planning_steps", "executive_shopping_list", "executive_money_calculation", 
            "executive_time_logic", "executive_logical_reasoning", "executive_analogies", 
            "executive_abstractions", "executive_intrusos", "executive_math_advanced"
        ) -> {
            val gameViewModel = remember { ExecutiveFunctionsViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { gameViewModel.startNewGame(exercise.exerciseType, challengeLevel, sessionId) }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = if (gameState.errorsCount == 0) 1 else 0
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            ExecutiveFunctionsGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
        exercise.exerciseType == "perception_color_identification" -> {
            val gameViewModel = remember { ColorIdentificationViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { gameViewModel.startNewGame(challengeLevel, sessionId) }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = if (gameState.errorsCount == 0) 1 else 0
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            ColorIdentificationGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
        exercise.exerciseType == "perception_size_ordering" -> {
            val gameViewModel = remember { SizeOrderingViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { gameViewModel.startNewGame(challengeLevel, sessionId) }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = if (gameState.errorsCount == 0) 1 else 0
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            SizeOrderingGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
        exercise.exerciseType in listOf("perception_lateral_dominance", "perception_mirror", "perception_body_parts") -> {
            val gameViewModel = remember { PerceptionViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) {
                val type = when(exercise.exerciseType) {
                    "perception_lateral_dominance" -> PerceptionType.LATERAL_DOMINANCE
                    "perception_mirror" -> PerceptionType.MIRROR
                    "perception_body_parts" -> PerceptionType.BODY_PARTS
                    else -> PerceptionType.LATERAL_DOMINANCE
                }
                gameViewModel.startNewGame(type, challengeLevel, sessionId)
            }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = if (gameState.errorsCount == 0) 1 else 0
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            PerceptionGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
        exercise.exerciseType == "perception_shape_fitting" -> {
            val gameViewModel = remember { ShapeFittingViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { gameViewModel.startNewGame(challengeLevel, sessionId) }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = if (gameState.errorsCount == 0) 1 else 0
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            ShapeFittingGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
        exercise.exerciseType == "literacy_tracing" -> {
            val gameViewModel = remember { TracingViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { gameViewModel.startNewGame(challengeLevel, sessionId) }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = if (gameState.errorsCount == 0) 1 else 0
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            TracingGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
        exercise.exerciseType in listOf("literacy_tracing_basic", "literacy_complete_letters", "literacy_copy_words", "literacy_form_shapes") -> {
            val gameViewModel = remember { LiteracyViewModel(saveUseCase) }
            LaunchedEffect(exercise.id) { 
                val variation = when(exercise.exerciseType) {
                    "literacy_tracing_basic" -> LiteracyVariation.TRACING_BASIC
                    "literacy_complete_letters" -> LiteracyVariation.COMPLETE_LETTERS
                    "literacy_copy_words" -> LiteracyVariation.COPY_WORDS
                    "literacy_form_shapes" -> LiteracyVariation.FORM_SHAPES
                    else -> LiteracyVariation.TRACING_BASIC
                }
                gameViewModel.startNewGame(variation, challengeLevel, sessionId) 
            }
            val gameState by gameViewModel.uiState.collectAsState()
            LaunchedEffect(gameState.isCompleted) { 
                if (gameState.isCompleted) {
                    val duration = safeDurationSeconds(gameState.startTimeMs)
                    val hits = if (gameState.errorsCount == 0) 1 else 0
                    onExerciseCompleted(hits, gameState.errorsCount, duration)
                }
            }
            LiteracyGame(
                viewModel = gameViewModel, 
                patientId = patientId, 
                professionalId = professionalId, 
                appointmentId = appointmentId, 
                onBack = onAbort
            )
        }
    }
}

@Composable
private fun TransitionView(
    state: SessionRunnerUiState.Transition,
    onStart: () -> Unit,
    speechManager: SpeechManager
) {
    val message = if (state.isFirst) "¡Hola! Vamos a empezar la sesión." else "¡Muy bien! Vamos al siguiente ejercicio."
    val subMessage = "Siguiente actividad: ${state.nextExerciseName}"
    LaunchedEffect(Unit) { speechManager.speak("$message $subMessage") }
    Column(modifier = Modifier.fillMaxSize().padding(32.dp), horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center) {
        Surface(color = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.2f), shape = RoundedCornerShape(12.dp)) {
            Text("v1.3.51", modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp), style = MaterialTheme.typography.labelSmall)
        }
        Spacer(modifier = Modifier.height(24.dp))
        Text(message, style = MaterialTheme.typography.displaySmall, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center)
        Spacer(modifier = Modifier.height(16.dp))
        Text(subMessage, style = MaterialTheme.typography.headlineMedium, color = MaterialTheme.colorScheme.primary, textAlign = TextAlign.Center)
        Spacer(modifier = Modifier.height(48.dp))
        Button(onClick = onStart, modifier = Modifier.height(72.dp).width(240.dp), shape = RoundedCornerShape(24.dp)) {
            Icon(Icons.Default.PlayArrow, contentDescription = null)
            Spacer(modifier = Modifier.width(12.dp))
            Text("Empezar ahora", fontSize = 20.sp)
        }
    }
}

@Composable
private fun StatBox(label: String, value: String, color: Color) {
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        Text(text = value, style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.Bold), color = color)
        Text(text = label, style = MaterialTheme.typography.labelSmall, color = Color.Gray)
    }
}

@Composable
private fun ProfessionalHUD(onShowPanel: () -> Unit) {
    Box(modifier = Modifier.fillMaxSize().padding(16.dp), contentAlignment = Alignment.BottomStart) {
        SmallFloatingActionButton(onClick = onShowPanel, containerColor = MaterialTheme.colorScheme.secondary.copy(alpha = 0.5f)) {
            Icon(Icons.Default.Settings, contentDescription = "Panel Terapeuta")
        }
    }
}
